import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Eye, Video, CheckCircle, ArrowRight, Lock, Bitcoin, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const areas = [
  { name: 'Ansiedad y estrés', icon: '🌊', description: 'Manejo de la ansiedad generalizada, ataques de pánico, estrés crónico.' },
  { name: 'Depresión', icon: '☁️', description: 'Apoyo para estados depresivos, tristeza persistente, falta de motivación.' },
  { name: 'Duelo y pérdidas', icon: '🕯️', description: 'Acompañamiento en procesos de duelo por pérdida de seres queridos o situaciones.' },
  { name: 'Relaciones y pareja', icon: '💕', description: 'Conflictos de pareja, dependencia emocional, comunicación.' },
  { name: 'Autoestima', icon: '✨', description: 'Trabajo en autoimagen, seguridad personal, amor propio.' },
  { name: 'Trauma y TEPT', icon: '🛡️', description: 'Procesamiento de experiencias traumáticas, EMDR, terapia del trauma.' },
  { name: 'Adicciones', icon: '🔓', description: 'Sustancias, comportamientos adictivos, recuperación.' },
  { name: 'Adolescentes', icon: '🌱', description: 'Orientación para jóvenes, identidad, presión social, cambios vitales.' },
];

const faqs = [
  {
    question: '¿Realmente es anónimo?',
    answer: 'Sí. No solicitamos tu nombre real, RUT, ni documento de identidad. Solo necesitas un seudónimo para tu sesión. El pago puede realizarse incluso con Bitcoin para mayor privacidad.'
  },
  {
    question: '¿Tengo que encender mi cámara?',
    answer: 'No es obligatorio. Puedes mantener tu cámara apagada durante toda la sesión si así lo prefieres. Tu psicólogo te escuchará igualmente.'
  },
  {
    question: '¿Cómo funcionan los pagos?',
    answer: 'Aceptamos Webpay (débito/crédito), Flow, Khipu y Bitcoin. El pago se realiza por sesión. Se emite boleta electrónica independientemente del método de pago.'
  },
  {
    question: '¿Puedo cambiar de psicólogo?',
    answer: 'Sí, puedes cambiar de profesional en cualquier momento. No hay compromiso de continuidad con un mismo psicólogo.'
  },
];

const PersonasPage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="hero-bg py-20 md:py-28">
        <div className="container-max mx-auto px-4 md:px-8">
          <div className="max-w-3xl">
            <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-4">
              <Shield className="w-3 h-3 mr-1" />
              Para Personas
            </Badge>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold text-slate-900 mb-6">
              Terapia online sin exponer tu identidad
            </h1>
            <p className="text-xl text-slate-600 leading-relaxed mb-8">
              Accede a atención psicológica profesional usando solo un seudónimo. 
              Cámara opcional, sin grabaciones, pago anónimo disponible.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/agendar">
                <Button size="lg" className="btn-primary w-full sm:w-auto" data-testid="personas-agendar-btn">
                  Agendar mi sesión
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/profesionales">
                <Button size="lg" variant="outline" className="btn-secondary w-full sm:w-auto">
                  Ver profesionales
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* How Anonymity Works */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              ¿Cómo funciona el anonimato?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Diseñamos cada paso pensando en tu privacidad y tranquilidad.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="glass-card p-6 text-center">
              <CardContent className="p-0">
                <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-4">
                  <Eye className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">Seudónimo</h3>
                <p className="text-slate-600 text-sm">
                  Elige cualquier nombre ficticio. No pedimos datos reales de identificación.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card p-6 text-center">
              <CardContent className="p-0">
                <div className="w-16 h-16 rounded-2xl bg-sage-100 flex items-center justify-center mx-auto mb-4">
                  <Video className="w-8 h-8 text-sage-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">Cámara opcional</h3>
                <p className="text-slate-600 text-sm">
                  Puedes mantener tu cámara apagada. Solo audio si lo prefieres.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card p-6 text-center">
              <CardContent className="p-0">
                <div className="w-16 h-16 rounded-2xl bg-sand-200 flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-sand-700" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">Sin grabación</h3>
                <p className="text-slate-600 text-sm">
                  Las sesiones nunca se graban. Tu conversación es privada.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card p-6 text-center">
              <CardContent className="p-0">
                <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-4">
                  <Bitcoin className="w-8 h-8 text-teal-600" />
                </div>
                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">Pago anónimo</h3>
                <p className="text-slate-600 text-sm">
                  Opción de pago con Bitcoin para máxima privacidad financiera.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What We Work On */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              ¿Qué trabajamos?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Nuestros profesionales te acompañan en diversos procesos de bienestar emocional.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {areas.map((area) => (
              <Card key={area.name} className="card-hover border-0 shadow-soft">
                <CardContent className="p-6">
                  <span className="text-3xl mb-3 block">{area.icon}</span>
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">{area.name}</h3>
                  <p className="text-sm text-slate-600">{area.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8">
            * Recuerda que AnonimaMente no es un servicio de urgencias. Si necesitas ayuda inmediata,
            contacta a <a href="tel:600 360 7777" className="text-teal-600 hover:underline">Salud Responde: 600 360 7777</a>
          </p>
        </div>
      </section>

      {/* Pricing */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Precios orientativos
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              El valor final lo fija cada profesional según su experiencia y especialidad. 
              Estos son rangos referenciales.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <Card className="border-2 border-slate-100">
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-heading text-xl">Sesión Individual</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-4">
                  <span className="text-4xl font-bold text-slate-900">$25.000</span>
                  <span className="text-slate-500"> - $45.000</span>
                </div>
                <p className="text-sm text-slate-600 mb-6">CLP por sesión de 50 minutos</p>
                <ul className="space-y-3 text-sm text-left">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    Sesión de 50 minutos
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    100% anónima
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    Boleta incluida
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-teal-600 relative">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <Badge className="bg-teal-600 text-white">Más elegido</Badge>
              </div>
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-heading text-xl">Pack 4 Sesiones</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-4">
                  <span className="text-4xl font-bold text-teal-600">$90.000</span>
                  <span className="text-slate-500"> - $160.000</span>
                </div>
                <p className="text-sm text-slate-600 mb-6">CLP (ahorra hasta 15%)</p>
                <ul className="space-y-3 text-sm text-left">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    4 sesiones de 50 min
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    Mismo profesional
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    Precio preferencial
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-2 border-slate-100">
              <CardHeader className="text-center pb-4">
                <CardTitle className="font-heading text-xl">Sesión de Pareja</CardTitle>
              </CardHeader>
              <CardContent className="text-center">
                <div className="mb-4">
                  <span className="text-4xl font-bold text-slate-900">$40.000</span>
                  <span className="text-slate-500"> - $65.000</span>
                </div>
                <p className="text-sm text-slate-600 mb-6">CLP por sesión de 60-75 min</p>
                <ul className="space-y-3 text-sm text-left">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    Sesión extendida
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    Ambos anónimos
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-sage-500" />
                    Seudónimos separados
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-8">
            <Link to="/agendar">
              <Button className="btn-primary" data-testid="pricing-agendar-btn">
                Agendar ahora
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                Preguntas frecuentes
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`}
                  className="bg-white rounded-xl border-0 shadow-soft px-6"
                >
                  <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline py-6">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-600 pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>

            <div className="text-center mt-8">
              <Link to="/faq" className="text-teal-600 hover:underline inline-flex items-center gap-2">
                <HelpCircle className="w-4 h-4" />
                Ver todas las preguntas frecuentes
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-teal-600">
        <div className="container-max mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-6">
            Tu primer paso hacia el bienestar
          </h2>
          <p className="text-lg text-teal-100 max-w-2xl mx-auto mb-8">
            No necesitas dar explicaciones. Solo elige un seudónimo y comienza tu camino hacia una mejor salud mental.
          </p>
          <Link to="/agendar">
            <Button size="lg" className="bg-white text-teal-600 hover:bg-teal-50 rounded-full px-8 py-6 text-lg">
              Comenzar ahora
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PersonasPage;
